import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
  Plus,
  Pencil,
  Trash2,
  LogOut,
  ExternalLink,
  Loader2,
  Eye,
  KeyRound,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { adminApi, formatApiError } from "@/lib/adminApi";
import { PostEditor } from "@/components/PostEditor";
import { AdminEnquiries } from "@/components/AdminEnquiries";
import { ChangePasswordModal } from "@/components/ChangePasswordModal";
import { ASSETS } from "@/lib/site";
import { setSeo } from "@/lib/seo";

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "numeric",
        month: "short",
        year: "numeric",
      })
    : "";

export default function AdminDashboard() {
  const { admin, logout } = useAuth();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | {} (new) | post
  const [deletingId, setDeletingId] = useState(null);
  const [tab, setTab] = useState("posts");
  const [showChangePw, setShowChangePw] = useState(false);

  useEffect(() => {
    setSeo({ title: "Blog admin | Blue Shuck Consulting LTD" });
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await adminApi.get("/admin/blog/posts");
      setPosts(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Failed to load posts.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const handleDelete = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    setDeletingId(post.id);
    try {
      await adminApi.delete(`/admin/blog/posts/${post.id}`);
      toast.success("Post deleted.");
      setPosts((p) => p.filter((x) => x.id !== post.id));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Delete failed.");
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="sticky top-0 z-40 bg-white border-b border-slate-200">
        <div className="mx-auto max-w-6xl px-6 flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <img src={ASSETS.wolfNavy} alt="" className="h-8 w-8 object-contain" />
            <div className="leading-tight">
              <p className="font-heading font-700 text-brand-navy text-sm">Blog admin</p>
              <p className="text-xs text-slate-400">{admin?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Link
              data-testid="admin-view-site"
              to="/blog"
              className="hidden sm:inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-navy px-3 py-2 rounded-lg hover:bg-slate-100"
            >
              <ExternalLink className="h-4 w-4" />
              View blog
            </Link>
            <button
              data-testid="admin-change-password"
              onClick={() => setShowChangePw(true)}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-brand-navy px-3 py-2 rounded-lg hover:bg-slate-100"
            >
              <KeyRound className="h-4 w-4" />
              <span className="hidden sm:inline">Password</span>
            </button>
            <button
              data-testid="admin-logout"
              onClick={logout}
              className="inline-flex items-center gap-1.5 text-sm text-slate-600 hover:text-red-600 px-3 py-2 rounded-lg hover:bg-slate-100"
            >
              <LogOut className="h-4 w-4" />
              Sign out
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl px-6 py-10">
        <div className="mb-8 flex items-center gap-1 border-b border-slate-200">
          <button
            data-testid="admin-tab-posts"
            onClick={() => setTab("posts")}
            className={`px-4 py-3 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === "posts"
                ? "border-brand-navy text-brand-navy"
                : "border-transparent text-slate-500 hover:text-brand-navy"
            }`}
          >
            Blog posts
          </button>
          <button
            data-testid="admin-tab-enquiries"
            onClick={() => setTab("enquiries")}
            className={`px-4 py-3 text-sm font-medium -mb-px border-b-2 transition-colors ${
              tab === "enquiries"
                ? "border-brand-navy text-brand-navy"
                : "border-transparent text-slate-500 hover:text-brand-navy"
            }`}
          >
            Enquiries
          </button>
        </div>

        {tab === "enquiries" ? (
          <AdminEnquiries />
        ) : (
        <>
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading font-800 text-3xl text-brand-navy">Posts</h1>
            <p className="mt-1 text-sm text-slate-500">
              {posts.length} post{posts.length === 1 ? "" : "s"} · manage your insights
            </p>
          </div>
          <button
            data-testid="admin-new-post"
            onClick={() => setEditing({})}
            className="inline-flex items-center gap-2 bg-brand-navy text-white font-medium px-5 py-2.5 rounded-lg hover:bg-brand-navy-dark transition-colors"
          >
            <Plus className="h-4 w-4" />
            New post
          </button>
        </div>

        <div className="mt-8 rounded-2xl border border-slate-200 bg-white overflow-hidden">
          {loading ? (
            <div className="py-20 flex justify-center">
              <Loader2 className="h-7 w-7 animate-spin text-brand-blue" />
            </div>
          ) : posts.length === 0 ? (
            <div className="py-20 text-center text-slate-500">
              No posts yet. Click <strong>New post</strong> to write your first one.
            </div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {posts.map((post) => (
                <li
                  key={post.id}
                  data-testid={`admin-post-row-${post.slug}`}
                  className="flex items-center gap-4 px-5 py-4 hover:bg-slate-50"
                >
                  <div className="h-12 w-16 shrink-0 rounded-md bg-slate-100 overflow-hidden">
                    {post.cover_image && (
                      <img src={post.cover_image} alt="" className="h-full w-full object-cover" />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span
                        data-testid={`admin-post-status-${post.slug}`}
                        className={`text-[10px] font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full ${
                          post.status === "published"
                            ? "bg-green-100 text-green-700"
                            : "bg-amber-100 text-amber-700"
                        }`}
                      >
                        {post.status}
                      </span>
                      <span className="text-xs text-slate-400">{post.category}</span>
                      <span className="text-xs text-slate-300">·</span>
                      <span className="text-xs text-slate-400">{formatDate(post.published_at)}</span>
                    </div>
                    <p className="mt-1 font-medium text-brand-navy truncate">{post.title}</p>
                  </div>
                  <div className="flex items-center gap-1">
                    {post.status === "published" && (
                      <a
                        href={`/blog/${post.slug}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-brand-navy"
                        aria-label="View post"
                      >
                        <Eye className="h-4 w-4" />
                      </a>
                    )}
                    <button
                      data-testid={`admin-edit-${post.slug}`}
                      onClick={() => setEditing(post)}
                      className="p-2 rounded-lg text-slate-500 hover:bg-slate-100 hover:text-brand-blue"
                      aria-label="Edit post"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      data-testid={`admin-delete-${post.slug}`}
                      onClick={() => handleDelete(post)}
                      disabled={deletingId === post.id}
                      className="p-2 rounded-lg text-slate-500 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
                      aria-label="Delete post"
                    >
                      {deletingId === post.id ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <Trash2 className="h-4 w-4" />
                      )}
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
        </>
        )}
      </main>

      {editing !== null && (
        <PostEditor
          post={editing.id ? editing : null}
          onClose={() => setEditing(null)}
          onSaved={() => {
            setEditing(null);
            load();
          }}
        />
      )}

      {showChangePw && <ChangePasswordModal onClose={() => setShowChangePw(false)} />}
    </div>
  );
}
